import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuthContext } from '@/lib/AuthProvider';
import { 
  getUserProfile, 
  addAddress, 
  updateAddress, 
  deleteAddress, 
  updateUserProfile, 
  updateNotificationPreferences,
  UserProfile, 
  Address, 
  NotificationPreferences 
} from '@/services/userProfileService';
import { NewsletterService } from '@/services/newsletterService';

interface UserProfileContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  addUserAddress: (address: Omit<Address, 'id'>) => Promise<Address>;
  updateUserAddress: (address: Address) => Promise<Address>;
  deleteUserAddress: (addressId: string) => Promise<void>;
  updateUserProfileInfo: (updates: { name: string }) => Promise<UserProfile>;
  updateUserNotificationPreferences: (preferences: NotificationPreferences) => Promise<NotificationPreferences>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthContext();

  const loadUserProfile = async () => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    } catch (err) {
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time listener for user profile changes
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time listener
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          
          // Helper function to convert Firestore timestamp to Date
          const convertTimestamp = (timestamp: any): Date => {
            if (!timestamp) return new Date();
            if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
              return timestamp.toDate();
            }
            if (timestamp instanceof Date) return timestamp;
            if (typeof timestamp === 'string') {
              const date = new Date(timestamp);
              return isNaN(date.getTime()) ? new Date() : date;
            }
            if (typeof timestamp === 'number') return new Date(timestamp);
            return new Date();
          };

          const profile: UserProfile = {
            uid: docSnapshot.id,
            email: userData.email || user.email,
            name: userData.name || userData.displayName || user.email?.split('@')[0] || 'User',
            addresses: userData.addresses || [],
            notificationPreferences: userData.notificationPreferences || {
              orderUpdates: true,
              promotions: true,
              newsletters: true
            },
            createdAt: convertTimestamp(userData.createdAt),
            // LTV fields
            firstOrderDate: userData.firstOrderDate ? convertTimestamp(userData.firstOrderDate) : undefined,
            lastOrderDate: userData.lastOrderDate ? convertTimestamp(userData.lastOrderDate) : undefined,
            totalSpent: userData.totalSpent || 0,
            orderCount: userData.orderCount || 0,
            averageOrderValue: userData.averageOrderValue || 0,
            averageDaysBetweenOrders: userData.averageDaysBetweenOrders || 0,
            calculatedLTV: userData.calculatedLTV || 0,
            customerSegment: userData.customerSegment || 'new',
            isActive: userData.isActive !== false, // Default to true
            // Affiliate program field - this will update in real-time!
            isAffiliate: userData.isAffiliate || false
          };

          setUserProfile(profile);
        } else {
          setUserProfile(null);
        }
        setLoading(false);
      },
      (error) => {
        setError('Failed to load user profile');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount or user change
    return () => unsubscribe();
  }, [user]);

  // Function to manually refresh the profile
  const refreshProfile = async () => {
    await loadUserProfile();
  };

  // Add an address to the user profile
  const addUserAddress = async (address: Omit<Address, 'id'>): Promise<Address> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      const newAddress = await addAddress(user.uid, address);
      
      // Update local state to include the new address
      if (userProfile) {
        const updatedAddresses = address.isDefault 
          ? [...userProfile.addresses.map(addr => ({ ...addr, isDefault: false })), newAddress]
          : [...userProfile.addresses, newAddress];
          
        setUserProfile({
          ...userProfile,
          addresses: updatedAddresses
        });
      }
      
      return newAddress;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing address
  const updateUserAddress = async (address: Address): Promise<Address> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      const updatedAddress = await updateAddress(user.uid, address);
      
      // Update local state
      if (userProfile) {
        let updatedAddresses;
        if (updatedAddress.isDefault) {
          // If this address is being set as default, update all addresses
          updatedAddresses = userProfile.addresses.map(addr => {
            if (addr.id === updatedAddress.id) {
              // Update this address with all new data and set as default
              return { ...updatedAddress, isDefault: true };
            } else {
              // Set all other addresses as non-default
              return { ...addr, isDefault: false };
            }
          });
        } else {
          // If not setting as default, just update this address
          updatedAddresses = userProfile.addresses.map(addr => 
            addr.id === updatedAddress.id ? updatedAddress : addr
          );
        }
        
        setUserProfile({
          ...userProfile,
          addresses: updatedAddresses
        });
      }
      
      return updatedAddress;
    } finally {
      setLoading(false);
    }
  };

  // Delete an address
  const deleteUserAddress = async (addressId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      await deleteAddress(user.uid, addressId);
      
      // Update local state
      if (userProfile) {
        const deletedAddress = userProfile.addresses.find(addr => addr.id === addressId);
        const filteredAddresses = userProfile.addresses.filter(addr => addr.id !== addressId);
        
        // If we deleted the default address and have others, make the first one default
        if (deletedAddress?.isDefault && filteredAddresses.length > 0) {
          filteredAddresses[0].isDefault = true;
        }
        
        setUserProfile({
          ...userProfile,
          addresses: filteredAddresses
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Update user profile information
  const updateUserProfileInfo = async (updates: { name: string }): Promise<UserProfile> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      const updatedProfile = await updateUserProfile(user.uid, updates);
      
      // Update local state
      setUserProfile(updatedProfile);
      
      return updatedProfile;
    } finally {
      setLoading(false);
    }
  };

  // Update user notification preferences
  const updateUserNotificationPreferences = async (preferences: NotificationPreferences): Promise<NotificationPreferences> => {
    if (!user || !userProfile) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      
      // Check if newsletter preference changed
      const previousNewsletterPreference = userProfile.notificationPreferences.newsletters;
      const newNewsletterPreference = preferences.newsletters;
      const newsletterPreferenceChanged = previousNewsletterPreference !== newNewsletterPreference;
      
      // Update notification preferences in Firebase
      const updatedPreferences = await updateNotificationPreferences(user.uid, preferences);
      
      // Handle newsletter subscription/unsubscription if preference changed
      if (newsletterPreferenceChanged && userProfile.email) {
        try {
          if (newNewsletterPreference) {
            // User enabled newsletters - subscribe them
            const subscriptionResult = await NewsletterService.subscribe({
              email: userProfile.email,
              source: 'settings-page'
            });
            
            if (!subscriptionResult.success) {
              // Silent error handling - don't throw error as preference update was successful
            }
          } else {
            // User disabled newsletters - unsubscribe them
            const unsubscriptionResult = await NewsletterService.unsubscribe(userProfile.email);
            
            if (!unsubscriptionResult.success) {
              // Silent error handling - don't throw error as preference update was successful
            }
          }
        } catch (newsletterError) {
          // Silent error handling - don't throw error as preference update was successful
        }
      }
      
      // Update local state
      setUserProfile(prevUserProfile => ({
        ...prevUserProfile!,
        notificationPreferences: updatedPreferences
      }));
      
      return updatedPreferences;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    userProfile,
    loading,
    error,
    refreshProfile,
    addUserAddress,
    updateUserAddress,
    deleteUserAddress,
    updateUserProfileInfo,
    updateUserNotificationPreferences
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = (): UserProfileContextType => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
}; 